<template>
  <div>
    <MainHeader title="Terms Of Use">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view legal-doc-view">
      <div class="wrapper-card legal-doc-card">
        <div class="legal-doc-header">
          <h1 class="legal-doc-title">Lucky Friday Terms of Use</h1>
          <div class="legal-doc-dates">
            <span class="legal-date-badge">Effective: 04/09/2025</span>
            <span class="legal-date-badge">Updated: 04/09/2025</span>
          </div>
        </div>

        <p class="legal-body">
          Welcome to Lucky Friday ("we," "us," "our"). These Terms of Service
          ("Terms") govern your access to and use of the Lucky Friday web
          application, including all related features and services
          (collectively, the "Service"). By using the Service, you agree to be
          bound by these Terms.
        </p>
        <p class="legal-body">
          If you do not agree, please do not use the Service.
        </p>

        <div class="legal-section">
          <h3 class="legal-section-title">1. Eligibility</h3>
          <p class="legal-body">
            You must be at least 18 years old and capable of forming a binding
            contract to use the Service. By using the Service, you represent and
            warrant that you meet these requirements.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">2. Account Registration</h3>
          <p class="legal-body">
            You may be required to create an account using our authentication
            provider (Auth0) to access certain features. You are responsible for
            maintaining the confidentiality of your login credentials and for
            any activity under your account.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">3. Use of the Service</h3>
          <p class="legal-body">
            You agree to use the Service only for lawful purposes and in
            accordance with these Terms. You may not:
          </p>
          <ul class="legal-list">
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>
              Reverse engineer or attempt to extract the source code of the
              Service
            </li>
            <li>
              Interfere with the Service or attempt to bypass security or
              authentication measures
            </li>
          </ul>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">4. Third-Party Services</h3>
          <p class="legal-body">
            Lucky Friday integrates with third-party services, including but not
            limited to:
          </p>
          <ul class="legal-list">
            <li>Plaid Inc. for financial account aggregation</li>
            <li>Auth0 (Okta Inc.) for identity authentication</li>
            <li>Microsoft Azure for infrastructure hosting</li>
          </ul>
          <p class="legal-body">
            By using the Service, you also agree to be bound by the terms and
            privacy policies of these providers. We are not responsible for the
            content, accuracy, or policies of any third-party service.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">5. Data and Content</h3>
          <p class="legal-body">
            You retain ownership of any data you submit to the Service. By
            submitting data, you grant us a license to store, process, and use
            it solely to provide the Service.
          </p>
          <p class="legal-body">
            We reserve the right to remove or restrict access to content that we
            reasonably believe violates these Terms or applicable law.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">6. Security and Availability</h3>
          <p class="legal-body">
            We take reasonable measures to secure your data using encryption at
            rest and in transit. However, we cannot guarantee that unauthorized
            third parties will never be able to defeat those measures.
          </p>
          <p class="legal-body">
            We may suspend or restrict access to the Service for maintenance,
            security, or legal compliance without notice.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">7. Disclaimers</h3>
          <p class="legal-body">
            The Service is provided "as is" and "as available," without
            warranties of any kind, express or implied. We do not guarantee
            that:
          </p>
          <ul class="legal-list">
            <li>The Service will be error-free or uninterrupted</li>
            <li>Any specific budgeting outcomes will be achieved</li>
            <li>
              Third-party services (e.g., Plaid) will always be available or
              accurate
            </li>
          </ul>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">8. Limitation of Liability</h3>
          <p class="legal-body">
            To the fullest extent permitted by law, we will not be liable for:
          </p>
          <ul class="legal-list">
            <li>Any indirect, incidental, or consequential damages</li>
            <li>Loss of data, profits, or goodwill</li>
            <li>Any content or actions of third-party services</li>
          </ul>
          <p class="legal-body">
            Our total liability shall not exceed the amount you paid to use the
            Service (if any) in the 12 months preceding the claim.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">9. Termination</h3>
          <p class="legal-body">
            We may terminate or suspend your access to the Service at any time
            for any reason, including if you violate these Terms. Upon
            termination, your right to use the Service ends immediately.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">10. Changes to These Terms</h3>
          <p class="legal-body">
            We may modify these Terms at any time. If we do, we'll notify you
            via the Service or by email. Your continued use after changes means
            you accept the updated Terms.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">11. Governing Law</h3>
          <p class="legal-body">
            These Terms are governed by the laws of the State of Idaho, without
            regard to conflict of laws principles. You agree to resolve any
            disputes exclusively in the state or federal courts located in Ada
            County, Idaho.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">12. Contact Us</h3>
          <p class="legal-body">
            If you have questions about these Terms, contact us at:
          </p>
          <a
            href="mailto:support@luckyfridayapp.com"
            class="legal-contact-link"
          >
            <font-awesome-icon
              :icon="['fas', 'envelope']"
              style="margin-right: 6px"
            />
            support@luckyfridayapp.com
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'TermsOfUse',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    return { router };
  },
};
</script>

<style scoped>
.legal-doc-view {
  max-width: 600px;
  margin: 0 auto;
}
.legal-doc-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
  padding: 1.5rem;
}
.legal-doc-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1.5px solid #eeedfe;
}
.dark-theme .legal-doc-header {
  border-bottom-color: #2a3a4e;
}
.legal-doc-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.6rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.dark-theme .legal-doc-title {
  color: white;
}
.legal-doc-dates {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.legal-date-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: var(--purple-50);
  color: var(--color-accent-purple);
  padding: 2px 10px;
  border-radius: 20px;
}
.dark-theme .legal-date-badge {
  background: #1e1a40;
}
.legal-section {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .legal-section {
  border-bottom-color: #2a3a4e;
}
.legal-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.legal-section-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-accent-purple);
  margin: 0 0 0.6rem;
}
.dark-theme .legal-section-title {
  color: var(--color-accent-teal);
}
.legal-body {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  margin: 0 0 0.6rem;
}
.dark-theme .legal-body {
  color: #d4d2d2;
}
.legal-list {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  padding-left: 1.25rem;
  margin: 0.4rem 0;
}
.dark-theme .legal-list {
  color: #d4d2d2;
}
.legal-list li {
  margin-bottom: 0.5rem;
}
.legal-list li:last-child {
  margin-bottom: 0;
}
.legal-contact-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-accent-purple);
  text-decoration: none;
  background: var(--purple-50);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-pill);
  transition: background var(--transition-fast);
}
.legal-contact-link:hover {
  background: var(--purple-100);
}
.dark-theme .legal-contact-link {
  background: #1e1a40;
  color: var(--color-accent-teal);
}
</style>

<!-- <template>
  <div>
    <MainHeader title="Terms Of Use">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view support-page">
      <div class="wrapper-card closing-text ps-2 pe-2 small-grey-text">
        <div>
          <h3>Lucky Friday Terms Of Use</h3>
          <h4>Effective Date: 04/09/2025</h4>
          <h4>Last Updated: 04/09/2025</h4>
          <p>
            Welcome to Lucky Friday (“we,” “us,” “our”). These Terms of Service
            (“Terms”) govern your access to and use of the Lucky Friday web
            application, including all related features and services
            (collectively, the “Service”). By using the Service, you agree to be
            bound by these Terms.
          </p>
          <p>If you do not agree, please do not use the Service.</p>

          <h4>1. Eligibility</h4>
          <p>
            You must be at least 18 years old and capable of forming a binding
            contract to use the Service. By using the Service, you represent and
            warrant that you meet these requirements.
          </p>

          <h4>2. Account Registration</h4>
          <p>
            You may be required to create an account using our authentication
            provider (Auth0) to access certain features. You are responsible for
            maintaining the confidentiality of your login credentials and for
            any activity under your account.
          </p>

          <h4>3. Use of the Service</h4>
          <p>
            You agree to use the Service only for lawful purposes and in
            accordance with these Terms. You may not:
          </p>
          <ul>
            <li>Use the Service for any illegal or unauthorized purpose</li>
            <li>
              Reverse engineer or attempt to extract the source code of the
              Service
            </li>
            <li>
              Interfere with the Service or attempt to bypass security or
              authentication measures
            </li>
          </ul>

          <h4>4. Third-Party Services</h4>
          <p>
            Lucky Friday integrates with third-party services, including but not
            limited to:
          </p>
          <ul>
            <li>Plaid Inc. for financial account aggregation</li>
            <li>Auth0 (Okta Inc.) for identity authentication</li>
            <li>Microsoft Azure for infrastructure hosting</li>
          </ul>
          <p>
            By using the Service, you also agree to be bound by the terms and
            privacy policies of these providers. We are not responsible for the
            content, accuracy, or policies of any third-party service.
          </p>

          <h4>5. Data and Content</h4>
          <p>
            You retain ownership of any data you submit to the Service. By
            submitting data, you grant us a license to store, process, and use
            it solely to provide the Service.
          </p>
          <p>
            We reserve the right to remove or restrict access to content that we
            reasonably believe violates these Terms or applicable law.
          </p>

          <h4>6. Security and Availability</h4>
          <p>
            We take reasonable measures to secure your data using encryption at
            rest and in transit. However, we cannot guarantee that unauthorized
            third parties will never be able to defeat those measures.
          </p>
          <p>
            We may suspend or restrict access to the Service for maintenance,
            security, or legal compliance without notice.
          </p>

          <h4>7. Disclaimers</h4>
          <p>
            The Service is provided “as is” and “as available,” without
            warranties of any kind, express or implied. We do not guarantee
            that:
          </p>
          <ul>
            <li>The Service will be error-free or uninterrupted</li>
            <li>Any specific budgeting outcomes will be achieved</li>
            <li>
              Third-party services (e.g., Plaid) will always be available or
              accurate
            </li>
          </ul>

          <h4>8. Limitation of Liability</h4>
          <p>
            To the fullest extent permitted by law, we will not be liable for:
          </p>
          <ul>
            <li>Any indirect, incidental, or consequential damages</li>
            <li>Loss of data, profits, or goodwill</li>
            <li>Any content or actions of third-party services</li>
          </ul>
          <p>
            Our total liability shall not exceed the amount you paid to use the
            Service (if any) in the 12 months preceding the claim.
          </p>

          <h4>9. Termination</h4>
          <p>
            We may terminate or suspend your access to the Service at any time
            for any reason, including if you violate these Terms. Upon
            termination, your right to use the Service ends immediately.
          </p>

          <h4>10. Changes to These Terms</h4>
          <p>
            We may modify these Terms at any time. If we do, we’ll notify you
            via the Service or by email. Your continued use after changes means
            you accept the updated Terms.
          </p>

          <h4>11. Governing Law</h4>
          <p>
            These Terms are governed by the laws of the State of Idaho, without
            regard to conflict of laws principles. You agree to resolve any
            disputes exclusively in the state or federal courts located in Ada
            County, Idaho.
          </p>

          <h4>12. Contact Us</h4>
          <p>If you have questions about these Terms, contact us at:</p>
          <p>
            📧
            <a href="mailto:support@luckyfridayapp.com"
              >support@luckyfridayapp.com</a
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'User-Support',
  components: {
    MainHeader,
  },
  setup() {
    const router = useRouter();

    return {
      router,
    };
  },
};
</script>
<style scoped>
a {
  color: black;
}
</style> -->
